"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { useWaveTopVoters } from "@/hooks/useWaveTopVoters";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { SingleWaveDropVoter } from "./SingleWaveDropVoter";
interface SingleWaveDropVotersProps {
  readonly drop: ApiDrop;
}
export const SingleWaveDropVoters: React.FC<SingleWaveDropVotersProps> = t0 => {
  const $ = _c(38);
  const {
    drop
  } = t0;
  const {
    connectedProfile
  } = useAuth();
  const t1 = connectedProfile?.handle ?? undefined;
  let t2;
  if ($[0] !== drop.id || $[1] !== drop.wave.id || $[2] !== t1) {
    t2 = {
      waveId: drop.wave.id,
      connectedProfileHandle: t1,
      reverse: false,
      dropId: drop.id,
      sortDirection: "DESC",
      sort: "ABSOLUTE"
    };
    $[0] = drop.id;
    $[1] = drop.wave.id;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    voters,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading
  } = useWaveTopVoters(t2);
  let t3;
  if ($[4] !== fetchNextPage || $[5] !== hasNextPage || $[6] !== isFetchingNextPage || $[7] !== isLoading) {
    t3 = () => {
      if (hasNextPage && !isLoading && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[4] = fetchNextPage;
    $[5] = hasNextPage;
    $[6] = isFetchingNextPage;
    $[7] = isLoading;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const intersectionElementRef = useIntersectionObserver(t3);
  const [isVotersOpen, setIsVotersOpen] = useState(false);
  let t4;
  if ($[9] !== isVotersOpen) {
    t4 = () => setIsVotersOpen(!isVotersOpen);
    $[9] = isVotersOpen;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = `tw-w-full tw-px-4 tw-py-4 tw-flex tw-items-center tw-justify-between tw-text-left desktop-hover:hover:tw-bg-iron-900 tw-transition-colors tw-duration-300 tw-ease-out tw-border-0 ${isVotersOpen ? "tw-bg-iron-800" : "tw-bg-iron-950"}`;
  const t6 = `tw-text-sm tw-font-medium ${isVotersOpen ? "tw-text-iron-300" : "tw-text-iron-400"}`;
  let t7;
  if ($[11] !== t6) {
    t7 = <span className={t6}>Top voters</span>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = isVotersOpen ? 180 : 0;
  let t9;
  if ($[13] !== t8) {
    t9 = {
      rotate: t8
    };
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      duration: 0.3
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = `tw-w-4 tw-h-4 tw-flex-shrink-0 ${isVotersOpen ? "tw-text-iron-400" : "tw-text-iron-600"}`;
  let t12;
  if ($[16] !== t11) {
    t12 = <ChevronDownIcon className={t11} />;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t9) {
    t13 = <motion.div animate={t9} transition={t10}>{t12}</motion.div>;
    $[18] = t12;
    $[19] = t9;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t13 || $[22] !== t4 || $[23] !== t5 || $[24] !== t7) {
    t14 = <button onClick={t4} className={t5}>{t7}{t13}</button>;
    $[21] = t13;
    $[22] = t4;
    $[23] = t5;
    $[24] = t7;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== drop.wave.voting_credit_type || $[27] !== intersectionElementRef || $[28] !== isFetchingNextPage || $[29] !== isLoading || $[30] !== isVotersOpen || $[31] !== voters) {
    t15 = isVotersOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="tw-overflow-hidden"><div className="tw-max-h-[19.75rem] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 hover:tw-scrollbar-thumb-iron-300">{voters.length > 0 || isLoading ? <><div className="tw-divide-y tw-divide-solid tw-divide-iron-800 tw-divide-x-0">{voters.map((voter, index) => <SingleWaveDropVoter voter={voter} key={voter.voter.id} position={index + 1} creditType={drop.wave.voting_credit_type} />)}</div>{(isFetchingNextPage || isLoading) && <div className="tw-w-full tw-h-0.5 tw-bg-iron-800 tw-overflow-hidden"><div className="tw-w-full tw-h-full tw-bg-indigo-400 tw-animate-loading-bar" /></div>}<div ref={intersectionElementRef} /></> : <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6 tw-space-y-4 tw-text-iron-400"><div className="tw-relative tw-group"><div className="tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-primary-400/20 tw-via-indigo-500/10 tw-to-iron-800/10 tw-rounded-full tw-animate-[spin_4s_linear_infinite] group-hover:tw-from-primary-400/30" /><div className="tw-absolute tw-inset-0 tw-bg-gradient-to-tr tw-from-iron-800/10 tw-via-indigo-500/10 tw-to-primary-400/20 tw-rounded-full tw-animate-[spin_5s_linear_infinite] group-hover:tw-to-primary-400/30" /><div className="tw-absolute tw-inset-0 tw-bg-gradient-radial tw-from-primary-300/5 tw-to-transparent tw-animate-pulse" /><svg className="tw-size-8 tw-flex-shrink-0 tw-relative tw-text-white/60" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M22.7 13.5L20.7005 11.5L18.7 13.5M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.3019 3 18.1885 4.77814 19.7545 7.42909M12 7V12L15 14" /></svg></div><div className="tw-flex tw-flex-col tw-items-center tw-gap-y-2"><span className="tw-tracking-tight tw-text-base tw-font-semibold tw-bg-gradient-to-br tw-from-iron-200 tw-via-iron-300 tw-to-iron-400 tw-bg-clip-text tw-text-transparent">Be the First to Make a Vote</span><p className="tw-text-sm tw-text-iron-500 tw-text-center tw-mb-0 tw-max-w-64">Vote on this drop to see voter rankings appear here.</p></div></div>}</div></motion.div>;
    $[26] = drop.wave.voting_credit_type;
    $[27] = intersectionElementRef;
    $[28] = isFetchingNextPage;
    $[29] = isLoading;
    $[30] = isVotersOpen;
    $[31] = voters;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t15) {
    t16 = <AnimatePresence>{t15}</AnimatePresence>;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t14 || $[36] !== t16) {
    t17 = <div>{t14}{t16}</div>;
    $[35] = t14;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
};